import { createAction, createReducer, createSelector } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { receivedUser } from "./user"
import { collectionAnySelector, collectionSelector, metadataSelector } from "./selectors"

import { reject, find, orderBy, omit } from "lodash"
import { formatPaginationParams } from "helpers/pagination"

import { LOADING_INITIAL_STATE, isTimeout, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// ACTIONS
const requested = createAction("conversations/requested")
const received = createAction("conversations/received")
const failed = createAction("conversations/failed")
const cleaned = createAction("conversations/cleaned")

export const receivedWSUnviewedCount = createAction("conversations/WS/receivedUnviewedCount")
export const receivedWSConversation = createAction("conversations/WS/receivedConversation")
export const receivedTypingData = createAction("conversations/WS/typing")
export const receivedStatusData = createAction("status/WS/received")
export const addLocalMessage = createAction("messages/addLocalMessage")
export const markMessageFailed = createAction("messages/markMessageFailed")
export const clearTriggeredByWS = createAction("conversations/clearTriggeredByWS")
export const setEditingMessage = createAction("conversations/setEditingMessage")
export const clearEditingMessage = createAction("conversations/clearEditingMessage")

const requestedConversation = createAction("conversations/requestedConversation")
const receivedConversation = createAction("conversations/receivedConversation")
const failedConversation = createAction("conversations/failedConversation")

const createdConversation = createAction("conversations/createdConversation")
const updatedConversation = createAction("conversations/updatedConversation")
const deletedConversation = createAction("conversations/deletedConversation")
const cleanedConversation = createAction("conversations/cleanedConversation")

const requestedMessage = createAction("conversations/requestedMessage")
const receivedMessage = createAction("conversations/receivedMessage")
const sentMessage = createAction("conversations/sentMessage")
const editMessage = createAction("conversations/editMessage")
const deletedMessage = createAction("conversations/deletedMessage")
const failedMessage = createAction("conversations/failedMessage")
const cleanedMessage = createAction("conversations/cleanedMessage")

const resetTotalUnviewedMessages = createAction("conversations/resetTotalUnviewedMessages")
const decriseTotalUnviewedMessages = createAction("conversations/decriseTotalUnviewedMessages")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  typing: {},
  status: [],
  editingMessageId: null,
  editingMessageBody: "",
  conversation: {
    ...LOADING_INITIAL_STATE,
    id: null,
    contact: {},
    last_message: {},
    unviewed: null,
    created_at: null
  },
  metadata: {
    page: null,
    total_pages: null,
    total_results: null,
    next_page: null,
    prev_page: null,
    total_unviewed_messages: 0
  },

  messages: {
    items: [],
    message: LOADING_INITIAL_STATE,
    metadata: {
      page: null,
      total_pages: null,
      total_results: null,
      next_page: null,
      prev_page: null
    }
  }
}

const conversationsReducer = createReducer(initialState, {
  [setEditingMessage.type]: (state, action) => {
    state.editingMessageId = action.payload.messageId
    state.editingMessageBody = action.payload.messageBody
  },

  [clearEditingMessage.type]: (state) => {
    state.editingMessageId = null
    state.editingMessageBody = ""
  },
  [receivedTypingData.type]: (state, action) => {
    const { conversation_id, user_id, typing, user_name } = action.payload

    if (!state.typing[conversation_id]) {
      state.typing[conversation_id] = {}
    }

    // Store the full user typing data
    state.typing[conversation_id][user_id] = {
      isTyping: typing,
      user_id: user_id,
      userName: user_name // Add userName to the typing data
    }
  },
  [receivedStatusData.type]: (state, action) => {
    const { id, status } = action.payload
    // Store the status keyed by id
    state.status = {
      ...state.status,
      [id]: status
    }
  },
  [requested.type]: (state, action) => {
    if (isTimeout(state)) action.abort = true
    else setRequested(state, true)
  },
  [received.type]: (state, action) => {
    const shouldAppend = action?.props?.append ?? false
    const newItems = Array.isArray(action.payload?.data) ? action.payload.data : []
    if (shouldAppend) {
      const existingIds = new Set(state.items.map((item) => item.id))
      const mergedItems = [...state.items, ...newItems.filter((item) => !existingIds.has(item.id))]
      state.items = orderBy(mergedItems, "updated_message_at", "desc")
    } else {
      state.items = orderBy(newItems, "updated_message_at", "desc")
    }
    Object.assign(state.metadata, action.payload.metadata)
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,

  [requestedConversation.type]: (state, action) => {
    setRequested(state.conversation, true)
    if (action.props?.reset) state.messages.items = []
  },
  [receivedConversation.type]: (state, action) => {
    setReceived(state.conversation)
    state.items.map((item) => Object.assign(item, { active: false }))
    const conversation = find(state.items, ["id", action.payload.id])
    Object.assign(conversation, omit(action.payload, "messages"), { active: true })

    state.items = orderBy(state.items, "updated_message_at", "desc")
    state.messages.items = action.props?.reset ? action.payload.messages.data : [...action.payload.messages.data, ...state.messages.items]
    Object.assign(state.messages.metadata, action.payload.messages.metadata)
  },
  [createdConversation.type]: (state) => {
    setReceived(state.conversation)
  },
  [updatedConversation.type]: (state, action) => {
    setReceived(state.conversation)
    const conversation = find(state.items, ["id", action.payload.id]) || {}
    const conversationId = conversation.id

    Object.assign(conversation, action.payload)
    state.items = orderBy(conversationId ? state.items : [action.payload, ...state.items], "updated_message_at", "desc")
  },
  [deletedConversation.type]: (state, action) => {
    setReceived(state.conversation)
    state.items = reject(state.items, ["id", action.payload.id])
  },
  [failedConversation.type]: (state) => {
    setFailed(state.conversation)
  },
  [cleanedConversation.type]: (state) => {
    state.conversation = initialState.conversation
  },
  [requestedMessage.type]: (state) => {
    setRequested(state.messages.message)
  },
  [receivedMessage.type]: (state, action) => {
    setRequested(state.messages.message)
    const activeConversation = find(state.items, "active") || {}
    if (activeConversation.id === action.payload.conversation_id) state.messages.items = [...state.messages.items, action.payload]
  },
  [sentMessage.type]: (state, action) => {
    const incomingMessage = action.payload
    const isRetry = action.props?.retry

    // Store the parameter indicating the WebSocket trigger
    state.triggeredByWS = true

    // Remove any existing message with the same body (e.g., failed optimistic message)
    state.messages.items = state.messages.items.filter((msg) => {
      // If retry is true, remove failed messages with the same body
      if (isRetry) {
        return !(msg.failed && msg.uuid === incomingMessage.uuid)
      }
      return true
    })

    // Update conversation
    const conversation = find(state.items, ["id", incomingMessage.conversation_id])
    if (conversation) {
      conversation.updated_message_at = incomingMessage.created_at
      conversation.last_message = incomingMessage
    }
    // Sort conversations
    state.items = orderBy(state.items, "updated_message_at", "desc")
  },
  [editMessage.type]: (state, action) => {
    const updatedMessage = action.payload
    const messageIndex = state.messages.items.findIndex((msg) => msg.id === updatedMessage.id)

    if (messageIndex !== -1) {
      // Update the specific message
      state.messages.items[messageIndex] = {
        ...state.messages.items[messageIndex],
        ...updatedMessage,
        is_edited: true
      }
    }
  },

  [failedMessage.type]: (state) => {
    setFailed(state.messages.message)
  },
  [addLocalMessage.type]: (state, action) => {
    state.messages.items.push(action.payload)
  },

  [markMessageFailed.type]: (state, action) => {
    const message = state.messages.items.find((m) => m.id === action.payload.tempId)
    if (message) {
      message.failed = true
    }
  },
  [cleanedMessage.type]: (state) => {
    state.messages.message = initialState.messages.message
  },
  [receivedUser.type]: (state, action) => {
    const { unread_messages_count = 0 } = action.payload?.metadata || {}
    if (unread_messages_count) state.metadata.total_unviewed_messages = unread_messages_count
  },
  [receivedWSUnviewedCount.type]: (state, action) => {
    state.metadata.total_unviewed_messages = action.payload
  },
  [clearTriggeredByWS.type]: (state) => {
    state.triggeredByWS = false
  },
  [receivedWSConversation.type]: (state, action) => {
    const receivedConversation = action.payload.data || {}
    const receivedLastMessage = receivedConversation.last_message || {}

    if (receivedConversation.id) {
      const conversation = find(state.items, ["id", receivedConversation.id]) || {}
      const conversationId = conversation.id

      Object.assign(conversation, action.payload.data)
      state.items = orderBy(conversationId ? state.items : [action.payload.data, ...state.items], "updated_message_at", "desc")
    }

    if (receivedLastMessage.id) {
      const activeConversation = find(state.items, "active") || {}
      if (activeConversation.id === receivedLastMessage.conversation_id) {
        // Filter out any existing message with the same ID
        const filteredMessages = state.messages.items.filter((msg) => msg.id !== receivedLastMessage.id)
        // Append the new message to the end
        state.messages.items = [...filteredMessages, receivedLastMessage]
        // If the last message has viewed: true, update all messages to viewed: true
        if (receivedLastMessage.viewed) {
          state.messages.items = state.messages.items.map((msg) => ({
            ...msg,
            viewed: true
          }))
        }
      }
    }

    Object.assign(state.metadata, action.payload.metadata)
  },

  [resetTotalUnviewedMessages.type]: (state) => {
    state.metadata.total_unviewed_messages = 0
  },

  [decriseTotalUnviewedMessages.type]: (state, { payload }) => {
    state.metadata.total_unviewed_messages = payload
  }
})
export default conversationsReducer

const getItems = (url, nextPage, params = {}, dispatch, getState) => {
  const state = getState()
  const { append, ...otherParams } = params
  const paginationParams = formatPaginationParams(state.conversations.metadata, nextPage)

  return dispatch(
    api({
      url,
      onSuccess: received.type,
      onError: failed.type,
      params: { ...paginationParams, ...otherParams }, // no append here
      props: { append: !!append }
    })
  )
}

export const getConversations = (nextPage, params) => (dispatch, getState) =>
  getItems("/conversations", nextPage, params, dispatch, getState)

export const getSearchConversation = (nextPage, params) => (dispatch, getState) =>
  getItems("/conversations/conversation_search", nextPage, params, dispatch, getState)

export const getConversation = (id, nextPage) => (dispatch, getState) => {
  const state = getState()
  return dispatch(
    api({
      url: `/conversations/${id}`,
      onStart: requestedConversation.type,
      onSuccess: receivedConversation.type,
      onError: failedConversation.type,
      params: formatPaginationParams(state.conversations.messages.metadata, nextPage),
      props: { reset: !nextPage }
    })
  )
}

export const createConversation = (data) =>
  api({
    url: "/conversations",
    method: "post",
    onStart: requestedConversation.type,
    onSuccess: createdConversation.type,
    onError: failedConversation.type,
    data
  })

export const updateConversation = (id, data) =>
  api({
    url: `/conversations/${id}`,
    method: "put",
    data: data,
    onStart: requestedConversation.type,
    onSuccess: updatedConversation.type,
    onError: failedConversation.type
  })

export const deleteConversation = (id) =>
  api({
    url: `/conversations/${id}`,
    method: "delete",
    onStart: requestedConversation.type,
    onSuccess: deletedConversation.type,
    onError: failedConversation.type
  })

export const cleanConversations = () => cleaned()
export const cleanConversation = () => cleanedConversation()

export const sendMessage = (conversationId, data, retry) => (dispatch) => {
  return dispatch(
    api({
      url: `/conversations/${conversationId}/messages`,
      method: "post",
      data,
      onStart: requestedMessage.type,
      onSuccess: sentMessage.type,
      onError: failedMessage.type,
      props: { retry: retry } // Pass retry from params
    })
  )
}

export const updateMessage = (messageId, data, retry) => (dispatch) => {
  return dispatch(
    api({
      url: `/messages/${messageId}`,
      method: "put",
      data,
      onStart: requestedMessage.type,
      onSuccess: editMessage.type,
      onError: failedMessage.type,
      props: { retry: retry } // Pass retry from params
    })
  )
}

export const deleteMessage = (conversationId, message_id) =>
  api({
    url: `/conversations/${conversationId}/delete_message`,
    method: "delete",
    data: { message_id },
    onStart: requestedMessage.type,
    onSuccess: deletedMessage.type,
    onError: failedMessage.type
  })

export const resetUnviewedMessagesCount = () => (dispatch) => {
  dispatch(resetTotalUnviewedMessages())
}

export const setViewedMessagesCount = (count) => (dispatch) => {
  dispatch(decriseTotalUnviewedMessages(count))
}

// SELECTORS
export const activeConversationSelector = createSelector(
  collectionSelector("conversations"),
  (conversations) => find(conversations, "active") || {}
)

export const messagesSelector = createSelector((state) => state.conversations, collectionSelector("messages"))
export const messagesAnySelector = createSelector((state) => state.conversations, collectionAnySelector("messages"))
export const messagesMetadataSelector = createSelector((state) => state.conversations, metadataSelector("messages"))
export const isMyMessageSelector = (message) =>
  createSelector(
    (state) => state.user.id,
    (userId) => message.author_id === userId
  )
export const typingSelector = (conversationId) =>
  createSelector(
    (state) => state.conversations.typing,
    (typing) => typing[conversationId] || {}
  )
